import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import type { ComplianceEvent } from '@/types/compliance';
import { useNavigate } from 'react-router-dom';
import { useTenancy } from '@/contexts/TenancyContext';

interface CorrectiveActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEvent?: ComplianceEvent;
  onDataChanged?: () => void;
}

const CorrectiveActionDialog: React.FC<CorrectiveActionDialogProps> = ({
  open,
  onOpenChange,
  selectedEvent
}) => {
  if (!selectedEvent) return null;
  const navigate = useNavigate();
  const { users, userIdToName } = useTenancy();

  const [summary, setSummary] = React.useState('');
  const [dueDate, setDueDate] = React.useState<Date | undefined>(undefined);
  const [assignee, setAssignee] = React.useState('');
  const [statusValue, setStatusValue] = React.useState('Open');

  // Helper function to find user ID from display name
  const findUserIdFromName = React.useCallback((displayName: string): string => {
    // First check if it's already a user ID
    if (userIdToName?.[displayName] || users.some(u => u.id === displayName)) {
      return displayName;
    }
    
    // Search for user by display name
    const user = users.find(u => {
      const fullName = `${u.firstName} ${u.lastName}`.trim();
      return fullName === displayName || userIdToName?.[u.id] === displayName;
    });
    
    return user?.id || displayName;
  }, [users, userIdToName]);

  React.useEffect(() => {
    if (!open || !selectedEvent) return;
    console.log('[CorrectiveActionDialog] Selected event:', selectedEvent);
    
    setSummary(selectedEvent.title || '');
    setDueDate(selectedEvent.nextReviewDate ? new Date(selectedEvent.nextReviewDate) : undefined);
    
    // Handle both assignee and owner fields for backward compatibility
    const rawAssigneeValue = (selectedEvent as any).assignee || (selectedEvent as any).owner || '';
    console.log('[CorrectiveActionDialog] Raw assignee value from event:', rawAssigneeValue);
    
    // Always use the raw value as-is since we're now storing user IDs
    // But handle backward compatibility for old data that might have display names
    const finalAssigneeValue = rawAssigneeValue.includes(' ') && 
                              !users.some(u => u.id === rawAssigneeValue) && 
                              !userIdToName?.[rawAssigneeValue]
                              ? findUserIdFromName(rawAssigneeValue)
                              : rawAssigneeValue;
    
    console.log('[CorrectiveActionDialog] Final assignee value:', finalAssigneeValue);
    setAssignee(finalAssigneeValue);
    
    setStatusValue(((selectedEvent.rawData as any)?.status || 'Open').toString());
  }, [open, selectedEvent, findUserIdFromName, users, userIdToName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card overflow-hidden">
          <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle data-force-isms-override>Corrective Action</DialogTitle>
            <DialogDescription data-force-isms-override>
              {selectedEvent.description}
            </DialogDescription>
          </div>

          <div className="px-4">
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="summary" className="text-right text-foreground">Summary</Label>
                <Textarea id="summary" value={summary} onChange={(e) => setSummary(e.target.value)} readOnly className="col-span-3 border border-gray-200 rounded-md resize-y min-h-[40px] max-h-[200px]" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="due-date" className="text-right text-foreground">Due Date</Label>
                <div className="col-span-3">
                  <DatePicker date={dueDate} setDate={setDueDate} fullWidth enableNaturalInput readOnly showPreview placeholder="Type 'tomorrow' or pick a date" />
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="assignee" className="text-right text-foreground">Assignee</Label>
                <div className="col-span-3 w-full">
                  <SingleUserCombobox 
                    selected={assignee} 
                    setSelected={setAssignee} 
                    readOnly 
                    placeholder={assignee || 'Select assignee...'} 
                    className="w-full" 
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="status" className="text-right text-foreground">Status</Label>
                <Input id="status" value={statusValue} readOnly className="col-span-3" />
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
            <div className="text-xs text-primary mt-2 ml-1 cursor-pointer">
            </div>
            <div className="flex flex-row gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button onClick={() => navigate(`/auditcentre?tab=corrective-actions&id=${selectedEvent?.id}`)}>
                View in Audit Centre
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CorrectiveActionDialog;
